import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import useAuthStore from "../store/authStore";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import {
  Loader2, Upload, CheckCircle2, ChevronRight, ChevronLeft, Shield, Wallet, GraduationCap,
  Clock, Camera, FileText, Bike, ArrowRight, XCircle,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/agent-onboarding`;

const CITY_OPTIONS = ["Bangalore", "Hyderabad", "Mumbai", "Delhi", "Chennai", "Pune", "Kolkata", "Ahmedabad", "Other"];

async function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function Stepper({ step, status }) {
  const steps = [
    { key: "apply", label: "Apply", icon: Bike },
    { key: "kyc", label: "KYC", icon: Shield },
    { key: "training", label: "Training", icon: GraduationCap },
    { key: "deposit", label: "Deposit", icon: Wallet },
    { key: "review", label: "Review", icon: CheckCircle2 },
  ];
  return (
    <div className="flex items-center justify-between gap-1 max-w-2xl mx-auto mb-8">
      {steps.map((s, i) => {
        const done = i < step;
        const current = i === step;
        return (
          <div key={s.key} className="flex items-center gap-1 flex-1">
            <div className={`flex flex-col items-center gap-1 flex-1 ${current ? "" : "opacity-80"}`} data-testid={`wizard-step-${s.key}`}>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-emerald-500 text-white" : current ? "bg-[#FF6B00] text-white" : "bg-slate-200 text-slate-500"}`}>
                {done ? <CheckCircle2 className="h-5 w-5" /> : <s.icon className="h-4 w-4" />}
              </div>
              <p className={`text-[10px] font-bold uppercase tracking-wider ${current ? "text-[#FF6B00]" : done ? "text-emerald-600" : "text-slate-400"}`}>{s.label}</p>
            </div>
            {i < steps.length - 1 && <div className={`h-0.5 flex-1 ${done ? "bg-emerald-500" : "bg-slate-200"}`}></div>}
          </div>
        );
      })}
    </div>
  );
}

export default function BecomeAgentApply() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuthStore();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // Step 0: apply form
  const [apply, setApply] = useState({
    name: "", email: "", password: "", phone: "", city: "Bangalore", vehicle_type: "2wheeler",
  });

  // Step 1: KYC
  const [kyc, setKyc] = useState({
    aadhaar_number: "", dl_number: "", vehicle_number: "", bank_account_number: "", bank_ifsc: "",
  });
  const [docs, setDocs] = useState({ aadhaar_doc: "", dl_doc: "", rc_doc: "", profile_photo: "" });

  // Step 2: training
  const [lessons, setLessons] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [lessonProgress, setLessonProgress] = useState({});

  // If user is already auth'd, load status
  useEffect(() => {
    (async () => {
      const tok = localStorage.getItem("pickngo_token");
      if (tok && user?.role === "agent") {
        try {
          const { data } = await api.get("/agent-onboarding/status");
          setStatus(data);
          if (data.status === "kyc_pending") setStep(1);
          else if (data.status === "training_pending") setStep(2);
          else if (data.status === "deposit_pending") setStep(3);
          else if (["under_review", "approved", "rejected"].includes(data.status)) setStep(4);
        } catch (err) {
          // No onboarding record yet — first-time applicant; stay on step 0.
          if (err.response?.status && err.response.status !== 404) {
            console.warn("agent-onboarding/status:", err.response.data?.detail || err.message);
          }
        }
      }
    })();
  }, [user]);

  const refreshStatus = async () => {
    const { data } = await api.get("/agent-onboarding/status");
    setStatus(data);
    return data;
  };

  // ------ Step 0: Apply ------
  const doApply = async (e) => {
    e.preventDefault();
    if (!apply.name || !apply.email || !apply.password || !apply.phone) {
      toast.error("All fields are required");
      return;
    }
    if (apply.password.length < 6) { toast.error("Password must be 6+ characters"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/apply`, apply);
      toast.success("Application started!");
      localStorage.setItem("pickngo_token", data.token);
      // Persist only the minimal public user shape — keep PII (email, phone)
      // out of localStorage. The full profile is hydrated by checkAuth() below
      // via /auth/me.
      localStorage.setItem("pickngo_user", JSON.stringify({ id: data.id, name: data.name, role: "agent", is_verified: false }));
      await checkAuth();
      setStep(1);
      setTimeout(refreshStatus, 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally { setLoading(false); }
  };

  // ------ Step 1: KYC ------
  const handleFile = async (field, file) => {
    if (!file) return;
    if (file.size > 3_500_000) { toast.error("File too large (max 3.5 MB)"); return; }
    const b64 = await toBase64(file);
    setDocs((p) => ({ ...p, [field]: b64 }));
  };

  const submitKYC = async (e) => {
    e.preventDefault();
    if (!kyc.aadhaar_number || !kyc.dl_number || !kyc.vehicle_number || !kyc.bank_account_number || !kyc.bank_ifsc) {
      toast.error("All KYC fields are required"); return;
    }
    if (!docs.aadhaar_doc || !docs.dl_doc || !docs.rc_doc || !docs.profile_photo) {
      toast.error("All 4 documents are required"); return;
    }
    setLoading(true);
    try {
      await api.post("/agent-onboarding/upload-kyc", {
        aadhaar_number: kyc.aadhaar_number,
        aadhaar_doc_base64: docs.aadhaar_doc,
        dl_number: kyc.dl_number,
        dl_doc_base64: docs.dl_doc,
        vehicle_number: kyc.vehicle_number,
        rc_doc_base64: docs.rc_doc,
        profile_photo_base64: docs.profile_photo,
        bank_account_number: kyc.bank_account_number,
        bank_ifsc: kyc.bank_ifsc,
      });
      toast.success("KYC uploaded!");
      await refreshStatus();
      setStep(2);
      const { data } = await api.get("/agent-onboarding/training/lessons");
      setLessons(data.lessons || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "KYC upload failed");
    } finally { setLoading(false); }
  };

  // ------ Step 2: Training ------
  useEffect(() => {
    if (step === 2 && !lessons.length && localStorage.getItem("pickngo_token")) {
      api.get("/agent-onboarding/training/lessons").then(({ data }) => setLessons(data.lessons || []));
    }
    if (status?.training_progress) setLessonProgress(status.training_progress);
  }, [step, status]);

  const completeLesson = async (id) => {
    try {
      const { data } = await api.post("/agent-onboarding/training/complete", { lesson_id: id });
      setLessonProgress(data.progress);
      toast.success(`Lesson ${id} complete!`);
      setActiveLesson(null);
      setActiveSlide(0);
      if (data.all_completed) {
        await refreshStatus();
        setStep(3);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // ------ Step 3: Deposit ------
  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const payDeposit = async () => {
    setLoading(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Couldn't load Razorpay"); setLoading(false); return; }
      const { data } = await api.post("/agent-onboarding/deposit/create-order");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "PickNGo Agent Deposit",
        description: "₹500 refundable deposit (auto-refunded in 24h)",
        order_id: data.order_id,
        handler: async (resp) => {
          try {
            await api.post("/agent-onboarding/deposit/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Deposit paid! Application under review.");
            await refreshStatus();
            setStep(4);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
          }
        },
        prefill: { name: user?.name, email: user?.email, contact: status?.phone },
        theme: { color: "#FF6B00" },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create deposit order");
    } finally { setLoading(false); }
  };

  const DocUploadTile = ({ field, label }) => (
    <label className="flex flex-col gap-2 cursor-pointer" data-testid={`doc-upload-${field}`}>
      <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
      <div className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center gap-2 hover:border-[#FF6B00] transition-colors ${docs[field] ? "border-emerald-400 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`}>
        {docs[field] ? <>
          {docs[field].startsWith("data:image") ? <img src={docs[field]} alt="" className="h-16 w-16 rounded-lg object-cover" /> : <FileText className="h-8 w-8 text-emerald-600" />}
          <span className="text-xs font-semibold text-emerald-700">Uploaded ✓</span>
        </> : <>
          <Upload className="h-6 w-6 text-slate-400" />
          <span className="text-xs text-slate-500">Click to upload</span>
          <span className="text-[10px] text-slate-400">Max 3.5 MB</span>
        </>}
      </div>
      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleFile(field, e.target.files?.[0])} />
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/become-agent" className="flex items-center gap-2"><Logo size="sm" showText={false} /><span className="font-['Outfit'] text-base font-bold">Agent Portal</span></Link>
          {user?.role === "agent" && <span className="text-xs text-slate-500">Signed in: <span className="font-semibold">{user.email}</span></span>}
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Stepper step={step} />

        {/* Step 0: Apply */}
        {step === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8" data-testid="step-apply">
            <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-1">Start Your Application</h2>
            <p className="text-sm text-slate-500 mb-6">Takes 2 minutes. You'll continue KYC after.</p>
            <form onSubmit={doApply} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full Name</Label><Input value={apply.name} onChange={(e) => setApply({ ...apply, name: e.target.value })} data-testid="apply-name" /></div>
                <div><Label>Phone</Label><Input value={apply.phone} onChange={(e) => setApply({ ...apply, phone: e.target.value })} data-testid="apply-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={apply.email} onChange={(e) => setApply({ ...apply, email: e.target.value })} data-testid="apply-email" /></div>
                <div><Label>Password</Label><Input type="password" value={apply.password} onChange={(e) => setApply({ ...apply, password: e.target.value })} data-testid="apply-password" /></div>
                <div>
                  <Label>City</Label>
                  <select value={apply.city} onChange={(e) => setApply({ ...apply, city: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="apply-city">
                    {CITY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Vehicle Type</Label>
                  <select value={apply.vehicle_type} onChange={(e) => setApply({ ...apply, vehicle_type: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="apply-vehicle">
                    <option value="2wheeler">2-Wheeler (Bike/Scooter)</option>
                    <option value="van">Van</option>
                    <option value="truck">Truck</option>
                  </select>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 font-semibold" data-testid="apply-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to KYC <ChevronRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </form>
          </div>
        )}

        {/* Step 1: KYC */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8" data-testid="step-kyc">
            <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-1">KYC Documents</h2>
            <p className="text-sm text-slate-500 mb-6">Upload clear photos or scans. Reviewed within 24-48 hours.</p>
            <form onSubmit={submitKYC} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Aadhaar Number</Label><Input placeholder="XXXX XXXX XXXX" value={kyc.aadhaar_number} onChange={(e) => setKyc({ ...kyc, aadhaar_number: e.target.value })} data-testid="kyc-aadhaar-num" /></div>
                <div><Label>Driving Licence No.</Label><Input value={kyc.dl_number} onChange={(e) => setKyc({ ...kyc, dl_number: e.target.value })} data-testid="kyc-dl-num" /></div>
                <div><Label>Vehicle Number</Label><Input placeholder="KA-01-XY-1234" value={kyc.vehicle_number} onChange={(e) => setKyc({ ...kyc, vehicle_number: e.target.value })} data-testid="kyc-vehicle-num" /></div>
                <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                  <div><Label>Bank Account Number</Label><Input value={kyc.bank_account_number} onChange={(e) => setKyc({ ...kyc, bank_account_number: e.target.value })} data-testid="kyc-bank-num" /></div>
                  <div><Label>IFSC Code</Label><Input placeholder="HDFC0001234" value={kyc.bank_ifsc} onChange={(e) => setKyc({ ...kyc, bank_ifsc: e.target.value.toUpperCase() })} data-testid="kyc-ifsc" /></div>
                </div>
              </div>
              <div className="grid sm:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
                <DocUploadTile field="aadhaar_doc" label="Aadhaar" />
                <DocUploadTile field="dl_doc" label="Driving Licence" />
                <DocUploadTile field="rc_doc" label="Vehicle RC" />
                <DocUploadTile field="profile_photo" label="Profile Photo" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 font-semibold" data-testid="kyc-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Upload KYC <ChevronRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </form>
          </div>
        )}

        {/* Step 2: Training */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8" data-testid="step-training">
            <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-1">Quick Training</h2>
            <p className="text-sm text-slate-500 mb-6">3 short lessons. Complete all to unlock your deposit step.</p>
            {!activeLesson ? (
              <div className="space-y-3">
                {lessons.map((l) => {
                  const done = !!lessonProgress[String(l.id)];
                  return (
                    <div key={l.id} className={`p-4 rounded-xl border-2 flex items-center justify-between ${done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`} data-testid={`lesson-${l.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${done ? "bg-emerald-500 text-white" : "bg-[#FF6B00]/10 text-[#FF6B00]"}`}>
                          {done ? <CheckCircle2 className="h-5 w-5" /> : <GraduationCap className="h-5 w-5" />}
                        </div>
                        <div>
                          <p className="font-['Outfit'] font-bold text-slate-900">{l.title}</p>
                          <p className="text-xs text-slate-500">{l.slides.length} slides · {l.duration_min} min</p>
                        </div>
                      </div>
                      <Button onClick={() => { setActiveLesson(l); setActiveSlide(0); }} variant={done ? "outline" : "default"} className={`rounded-lg ${done ? "" : "bg-[#FF6B00] hover:bg-[#E65A00]"}`} data-testid={`start-lesson-${l.id}`}>
                        {done ? "Review" : "Start"} <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#FF6B00] mb-2">Lesson {activeLesson.id} · Slide {activeSlide + 1}/{activeLesson.slides.length}</p>
                <h3 className="font-['Outfit'] text-xl font-bold text-slate-900 mb-2">{activeLesson.slides[activeSlide].heading}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{activeLesson.slides[activeSlide].body}</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setActiveSlide((s) => Math.max(0, s - 1))} disabled={activeSlide === 0} className="rounded-lg" data-testid="slide-prev"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
                  {activeSlide < activeLesson.slides.length - 1 ? (
                    <Button onClick={() => setActiveSlide((s) => s + 1)} className="flex-1 bg-[#FF6B00] hover:bg-[#E65A00] rounded-lg" data-testid="slide-next">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
                  ) : (
                    <Button onClick={() => completeLesson(activeLesson.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid="lesson-complete-btn"><CheckCircle2 className="h-4 w-4 mr-1" /> Mark Complete</Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Deposit */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 text-center" data-testid="step-deposit">
            <Wallet className="h-12 w-12 text-[#FF6B00] mx-auto mb-3" />
            <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-1">₹500 Security Deposit</h2>
            <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">Pay ₹500 via Razorpay. It's <span className="font-bold text-emerald-600">auto-refunded within 24 hours</span> to your source account.</p>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 max-w-md mx-auto mb-6 text-left">
              <p className="text-sm font-bold text-emerald-900 flex items-center gap-1.5"><Clock className="h-4 w-4" /> Refund Timeline</p>
              <p className="text-xs text-emerald-800 mt-1">Your deposit will be auto-refunded to the same payment source within 24 hours of successful payment. No action needed.</p>
            </div>
            <Button onClick={payDeposit} disabled={loading} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-12 px-8 font-semibold" data-testid="pay-deposit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wallet className="h-4 w-4 mr-2" /> Pay ₹500 Deposit</>}
            </Button>
          </div>
        )}

        {/* Step 4: Review / Status */}
        {step === 4 && status && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-10 text-center" data-testid="step-review">
            {status.status === "under_review" && <>
              <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4"><Clock className="h-8 w-8 text-amber-600" /></div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-2">Application Under Review</h2>
              <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">Our team is verifying your documents. You'll be notified within 24-48 hours once approved.</p>
            </>}
            {status.status === "approved" && <>
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-8 w-8 text-emerald-600" /></div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-2">You're Approved!</h2>
              <p className="text-sm text-slate-500 mb-6">Sign in to your agent dashboard and start accepting jobs.</p>
              <Button onClick={() => navigate("/dashboard/orders")} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 px-8 font-semibold" data-testid="go-dashboard-btn">Go to Dashboard <ArrowRight className="h-4 w-4 ml-2" /></Button>
            </>}
            {status.status === "rejected" && <>
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4"><XCircle className="h-8 w-8 text-red-600" /></div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-2">Application Rejected</h2>
              <p className="text-sm text-slate-500 mb-3">Reason: <span className="font-semibold text-slate-900">{status.rejection_reason || "—"}</span></p>
              <p className="text-xs text-slate-400">Please contact support if you believe this is an error.</p>
            </>}
            {status.security_deposit_status === "paid" && <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100 max-w-md mx-auto text-left">
              <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Deposit refund auto-scheduled</p>
              <p className="text-[11px] text-emerald-700 mt-1">Refund will be processed to your source account by {status.security_deposit_refund_due_at?.slice(0, 16)?.replace("T", " ")} UTC</p>
            </div>}
            {status.security_deposit_status === "refunded" && <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100 max-w-md mx-auto">
              <p className="text-xs font-bold text-emerald-800 flex items-center justify-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> ₹500 Deposit Refunded</p>
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}
